import React, { useState, useRef, useEffect } from 'react';

interface PlaybackSpeedSelectorProps {
  currentSpeed: number;
  onSpeedChange: (speed: number) => void;
}

const SPEED_OPTIONS = [0.25, 0.5, 0.75, 1.0];

const PlaybackSpeedSelector: React.FC<PlaybackSpeedSelectorProps> = ({
  currentSpeed,
  onSpeedChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonWidth, setButtonWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Measure button width on mount
  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSpeedSelect = (speed: number) => {
    onSpeedChange(speed);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
      }}
    >
      {/* Current speed button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '6px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: '600',
          width: '36px',
          height: '36px',
          minWidth: 'unset',
          minHeight: 'unset',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {currentSpeed}x
      </button>

      {/* Dropdown menu */}
      {isOpen && buttonWidth > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '0',
            marginBottom: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '6px',
            overflow: 'hidden',
            zIndex: 1000,
            width: `${buttonWidth}px`,
            boxSizing: 'border-box',
          }}
        >
          {SPEED_OPTIONS.map((speed) => (
            <button
              key={speed}
              onClick={() => handleSpeedSelect(speed)}
              style={{
                backgroundColor: currentSpeed === speed ? 'rgba(99, 102, 241, 0.8)' : 'transparent',
                color: 'white',
                border: '1px solid transparent',
                padding: '6px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: currentSpeed === speed ? '600' : 'normal',
                width: '100%',
                height: '36px',
                minWidth: 'unset',
                minHeight: 'unset',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
              onMouseEnter={(e) => {
                if (currentSpeed !== speed) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentSpeed !== speed) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {speed}x
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlaybackSpeedSelector;
